import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import './App.css'
import ErrorBoundary from './components/ErrorBoundary'
import { DiscoverRefreshProvider } from './contexts/DiscoverRefreshContext'
import LoginPage from './pages/LoginPage'
import ArticlesPage from './pages/ArticlesPage'
import ArticleDetailPage from './pages/ArticleDetailPage'
import DiscoverPage from './pages/DiscoverPage'
import SettingsPage from './pages/SettingsPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import SharedArticlePage from './pages/SharedArticlePage'
import PrivacyPage from './pages/PrivacyPage'
import SupportPage from './pages/SupportPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth0()

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function ShareTargetRedirect() {
  const [params] = useSearchParams()
  const url = params.get('url') || ''
  // Redirect to home with share params so ArticlesPage can pre-fill
  return <Navigate to={`/?share_url=${encodeURIComponent(url)}`} replace />
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <DiscoverRefreshProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/share-target"
              element={
                <ProtectedRoute>
                  <ShareTargetRedirect />
                </ProtectedRoute>
              }
            />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ArticlesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discover"
              element={
                <ProtectedRoute>
                  <DiscoverPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/articles/:id"
              element={
                <ProtectedRoute>
                  <ArticleDetailPage />
                </ProtectedRoute>
              }
            />
            <Route path="/share/:token" element={<SharedArticlePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </DiscoverRefreshProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
